/**
 * When a provider was sent home ("Off") and called back, per day (Braden 2026-09-13).
 *
 * Stored beside the Off disposition in the shared `oc-anes-<date>` annotation store as `offLog[employeeId]`: a list
 * of local "HH:MM" times on that date. Setting Off opens an entry at the moment it is set; clearing Off (calling them
 * back) closes it. A pecking-order provider sent home at 11:00 and called back at 15:00 is one closed entry, and the
 * board's colouring treats 11:00-15:00 as time they are not present. Times can be corrected from the person card.
 */
export interface OffEntry { off: string; back?: string }
export type OffLog = Record<string, OffEntry[]>;

const HM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** The current local wall-clock time as "HH:MM". */
export const hmNow = (d: Date = new Date()): string =>
  `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

/** Record Off being set (`goingOff`) or cleared at `atHm`. A repeat of the current state changes nothing. */
export const recordOffToggle = (log: OffLog | undefined, employeeId: string, goingOff: boolean, atHm: string): OffLog => {
  const current = log || {};
  const entries = [...(current[employeeId] || [])];
  const last = entries[entries.length - 1];
  const isOpen = !!last && !last.back;
  if (goingOff && !isOpen) entries.push({ off: atHm });
  else if (!goingOff && isOpen) entries[entries.length - 1] = { ...last, back: atHm };
  else return current;
  return { ...current, [employeeId]: entries };
};

/** Correct one recorded time. A called-back time can only be edited once they have actually been called back. */
export const setOffEntryTime = (
  log: OffLog | undefined, employeeId: string, index: number, field: 'off' | 'back', atHm: string,
): OffLog => {
  const current = log || {};
  const entries = [...(current[employeeId] || [])];
  const entry = entries[index];
  if (!entry || !HM.test(atHm) || (field === 'back' && !entry.back)) return current;
  entries[index] = { ...entry, [field]: atHm };
  return { ...current, [employeeId]: entries };
};

/**
 * The spans a provider was away (sent home) as epoch-ms intervals, using `hmToMs` to place "HH:MM" on the day.
 * An Off saved before times were recorded (no entries, but currently Off) counts as the whole day, as it always did.
 */
export const awayIntervals = (
  entries: OffEntry[] | undefined, currentlyOff: boolean, hmToMs: (hm: string) => number,
): [number, number][] => {
  const valid = (entries || []).filter(e => HM.test(e.off));
  if (!valid.length) return currentlyOff ? [[-Infinity, Infinity]] : [];
  return valid.map((e): [number, number] => [hmToMs(e.off), e.back && HM.test(e.back) ? hmToMs(e.back) : Infinity]);
};
